import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useBoardStore } from '../../../stores/boardStore';
import { Lightbulb, CheckCircle2, Circle, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface BoardSetupStepsProps {
    hasAITask: boolean;
    isContentModalOpen: boolean;
    isHidden?: boolean;
    onOpenAddConnection: () => void;
    onOpenAddJournal: () => void;
    onOpenAddSpec: () => void;
    onOpenCreatePage: () => void;
    onOpenAI: () => void;
    onShowFeatureExplanation: (type: 'connections' | 'specs') => void;
}

export const BoardSetupSteps: React.FC<BoardSetupStepsProps> = ({ 
    hasAITask, 
    isContentModalOpen,
    isHidden = false,
    onOpenAddConnection,
    onOpenAddJournal,
    onOpenAddSpec,
    onOpenCreatePage,
    onOpenAI,
    onShowFeatureExplanation
}) => {
    const boardId = useBoardStore(state => state.boardId);
    const channels = useBoardStore(state => state.channels);
    const journals = useBoardStore(state => state.journals);
    const specs = useBoardStore(state => state.specs);
    const pages = useBoardStore(state => state.pages);
    const channelErrors = useBoardStore(state => state.channelErrors);

    const [isOpen, setIsOpen] = useState(false);
    const [isDismissed, setIsDismissed] = useState(true); // default true until checked
    const [isHiddenManually, setIsHiddenManually] = useState(false);
    const [isPulsing, setIsPulsing] = useState(false);
    
    const popupRef = useRef<HTMLDivElement>(null);
    const previousModalState = useRef(isContentModalOpen);

    // Initialize state from local storage
    useEffect(() => {
        if (!boardId) return;
        
        const dismissedKey = `dashbal_board_${boardId}_setup_dismissed`;
        const hiddenKey = `dashbal_board_${boardId}_setup_hidden`;
        
        const initialDismissed = localStorage.getItem(dismissedKey) === 'true';
        const initialHidden = localStorage.getItem(hiddenKey) === 'true';
        
        setIsDismissed(initialDismissed);
        setIsHiddenManually(initialHidden);

        // Always open on refresh if not dismissed and not hidden
        if (!initialDismissed && !initialHidden) {
            setIsOpen(true);
        }

    }, [boardId]);

    // Handle auto-open on first modal close
    useEffect(() => {
        if (!boardId || isDismissed || isHiddenManually) return;

        const wasOpen = previousModalState.current;
        const isClosedNow = !isContentModalOpen;
        
        const firstCloseKey = `dashbal_board_${boardId}_first_modal_closed`;
        const hasClosedBefore = localStorage.getItem(firstCloseKey) === 'true';

        if (wasOpen && isClosedNow && !hasClosedBefore) {
            // It's the first time the modal is closed
            localStorage.setItem(firstCloseKey, 'true');
            
            // Show auto-popup with animation
            setTimeout(() => {
                setIsOpen(true);
                setIsPulsing(true);
                setTimeout(() => setIsPulsing(false), 3000); // Stop pulse after 3s
            }, 500);
        }

        previousModalState.current = isContentModalOpen;
    }, [isContentModalOpen, boardId, isDismissed]);

    // Track when any auto-step becomes newly completed while card is not dismissed
    const getChannelCategory = (channel: any): 'ads' | 'sales' | 'other' => {
        const cat = channel?.channelTypeConfig?.category?.toLowerCase() || '';
        if (cat === 'advertising' || cat === 'marketing' || cat === 'ads' || ['meta', 'google_ads', 'tiktok'].includes(channel.channelType)) return 'ads';
        if (cat === 'sales' || cat === 'ecommerce' || ['shopify', 'woocommerce'].includes(channel.channelType)) return 'sales';
        if (cat) return 'sales'; // fallback to sales for things like stripe if they have category but not ads
        return 'other';
    };

    const adsChannels = channels.filter(c => getChannelCategory(c) === 'ads');
    const salesChannels = channels.filter(c => getChannelCategory(c) === 'sales');

    const autoSteps = useMemo(() => {
        return {
            ads: {
                isCompleted: adsChannels.length > 0,
                hasError: adsChannels.some(c => channelErrors[c.id])
            },
            sales: {
                isCompleted: salesChannels.length > 0,
                hasError: salesChannels.some(c => channelErrors[c.id])
            },
            journal: {
                isCompleted: journals.length > 0,
                hasError: false
            },
            spec: {
                isCompleted: specs.length > 0,
                hasError: specs.some(s => s.error)
            },
            page: {
                isCompleted: pages.length > 0, // usually there's at least 1, but we evaluate
                hasError: false
            },
            ai: {
                isCompleted: hasAITask,
                hasError: false
            }
        };
    }, [channels, journals, specs, pages, hasAITask, channelErrors]);

    // Re-open if an auto step is completed
    const prevAutoSteps = useRef(autoSteps);
    useEffect(() => {
        const prev = prevAutoSteps.current;
        // Always advance the snapshot so transitions that fire while the popup
        // is open are not "replayed" the next time the popup closes.
        prevAutoSteps.current = autoSteps;

        if (!boardId || isDismissed || isHiddenManually || isOpen) return;

        const newStepCompleted = (Object.keys(autoSteps) as Array<keyof typeof autoSteps>).some(
            key => !prev[key].isCompleted && autoSteps[key].isCompleted
        );

        if (newStepCompleted) {
            setIsOpen(true);
            setIsPulsing(true);
            setTimeout(() => setIsPulsing(false), 2000);
        }
    }, [autoSteps, boardId, isDismissed, isHiddenManually, isOpen]);



    const handleDismiss = () => {
        if (!boardId) return;
        localStorage.setItem(`dashbal_board_${boardId}_setup_dismissed`, 'true');
        setIsDismissed(true);
        setIsOpen(false);
    };

    const handleHide = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!boardId) return;
        localStorage.setItem(`dashbal_board_${boardId}_setup_hidden`, 'true');
        setIsHiddenManually(true);
        setIsOpen(false);
    };

    if (isDismissed) return null;

    const stepsList = [
        { id: 'ads', label: 'Connect an advertising channel', ...autoSteps.ads, action: onOpenAddConnection },
        { id: 'sales', label: 'Connect a sales channel', ...autoSteps.sales, action: onOpenAddConnection },
        { id: 'journal', label: 'Connect a money journal', ...autoSteps.journal, action: onOpenAddJournal },
        { id: 'spec', label: 'Add a board spec', ...autoSteps.spec, action: onOpenAddSpec },
        { 
            id: 'page', 
            label: 'Create your first page', 
            ...autoSteps.page, 
            action: () => {
                if (channels.length > 0) {
                    onOpenCreatePage();
                } else {
                    onShowFeatureExplanation('connections');
                }
            } 
        },
        { 
            id: 'ai', 
            label: 'Ask Dashbal AI to analyse', 
            ...autoSteps.ai, 
            action: () => {
                if (channels.length > 0) {
                    onOpenAI();
                } else {
                    onShowFeatureExplanation('connections');
                }
            } 
        },
    ];

    return (
        <div className="relative" ref={popupRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`h-9 w-9 shrink-0 flex items-center justify-center bg-white dark:bg-neutral-900 border ${
                    (isOpen && !isHidden) || isPulsing || isHiddenManually ? 'border-accent-primary bg-accent-primary/5 dark:bg-accent-primary/10 shadow-[0_0_15px_rgba(var(--color-accent-primary),0.3)]' : 'border-neutral-300 dark:border-neutral-800'
                } rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all relative`}
                title="Board Setup Steps"
            >
                <Lightbulb className={`w-4 h-4 ${((isOpen && !isHidden) || isPulsing || isHiddenManually) ? 'text-accent-primary ring-accent-primary' : 'text-neutral-500 dark:text-neutral-400'} ${isPulsing ? 'animate-pulse' : ''}`} />
                {stepsList.some(s => s.hasError) && !isPulsing && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full border border-white dark:border-neutral-900"></span>
                )}
            </button>

            <AnimatePresence>
                {(isOpen && !isHidden) && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-xl w-80 overflow-hidden origin-top-right"
                    >
                        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
                            <div className="flex items-center justify-between">
                                <h3 className="font-bold text-neutral-900 dark:text-white text-sm">
                                    Setup Guide
                                </h3>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={handleHide}
                                        className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 underline"
                                    >
                                        Hide
                                    </button>
                                    <button 
                                        onClick={handleDismiss}
                                        className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 underline"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                Complete these steps to get the most out of your board.
                            </p>
                        </div>
                        
                        <div className="p-2 max-h-[60vh] overflow-y-auto fade-scrollbar">
                            {stepsList.map(step => {
                                const isChecked = step.isCompleted;
                                
                                return (
                                    <div 
                                        key={step.id}
                                        onClick={(e) => {
                                            // Close setup steps menu if a step action takes place
                                            setIsOpen(false);
                                            e.stopPropagation();
                                            step.action();
                                        }}
                                        className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50`}
                                    >
                                        <div className="mt-0.5 shrink-0">
                                            {step.hasError ? (
                                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                                            ) : isChecked ? (
                                                <CheckCircle2 className="w-5 h-5 text-emerald-500 hover:text-emerald-400" />
                                            ) : (
                                                <Circle className="w-5 h-5 text-neutral-300 dark:text-neutral-600 hover:text-neutral-400 dark:hover:text-neutral-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm tracking-tight transition-colors ${
                                                step.hasError 
                                                    ? 'text-amber-600 dark:text-amber-500' 
                                                    : isChecked 
                                                        ? 'text-emerald-700 dark:text-emerald-500 line-through decoration-emerald-500/30' 
                                                        : 'text-neutral-700 dark:text-neutral-300 font-medium'
                                            }`}>
                                                {step.label}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
